import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  fetchSharedWithMe,
  fetchTeamAttendanceProgress,
  fetchAttendanceEventBriefing,
  fetchAttendanceEventLogoutSummary,
  fetchPendingLeaveApprovals,
  approveLeave,
  rejectLeave,
  fetchDayActivityTimeline,
  fetchTeamLiveStatus,
  startSharedUserBreak,
  endSharedUserBreak,
  markSharedUserOnLeave,
  revertSharedUserLeave,
} from '../../services/attendanceService';
import { SHORT_BREAK_REASONS } from './ShortBreakModal';
import { subscribeTeamLiveStatusChanged } from '../../services/attendanceSessionBus';
import LogToWorkModal from './LogToWorkModal';
import DayActivityTimelineModal from './DayActivityTimelineModal';
import MemberSummaryModal from './MemberSummaryModal';
import PresignedAvatar from '../common/PresignedAvatar';
import { TaskStatusIcon, formatTaskStatusLabel, TASK_STATUS_STYLES } from './attendanceTaskStatus';
import { presenceDotClass } from '../../utils/presenceLabelUtils';

const STATUS_STYLES = {
  CHECKED_IN: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  LOGGED_OUT: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const STATUS_LABELS = {
  CHECKED_IN: 'Checked in',
  LOGGED_OUT: 'Logged out',
};

const ATTENDANCE_STATE_STYLES = {
  CHECKED_IN: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  ON_BREAK: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  LOGGED_OUT: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  NOT_CHECKED_IN: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  ON_LEAVE: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
};

const ATTENDANCE_STATE_LABELS = {
  CHECKED_IN: 'Checked in',
  ON_BREAK: 'On break',
  LOGGED_OUT: 'Logged out',
  NOT_CHECKED_IN: 'Not checked in',
  ON_LEAVE: 'On leave',
};

const productivityColor = (pct) => {
  if (pct == null) return 'bg-gray-200 dark:bg-gray-700';
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-rose-500';
};

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const leaveSummaryLine = (leave) => {
  if (leave.eventType === 'LEAVE_REQUEST') {
    const range = leave.fromDateLabel && leave.toDateLabel
      ? (leave.fromDateLabel === leave.toDateLabel
        ? leave.fromDateLabel
        : `${leave.fromDateLabel} → ${leave.toDateLabel}`)
      : (leave.fromDate && leave.toDate ? `${leave.fromDate} → ${leave.toDate}` : '');
    return range || 'Leave request';
  }
  if (leave.leaveType === 'HALF_DAY') {
    return `${leave.leaveTypeLabel || 'Half day'} · ${leave.startTimeLabel || leave.startTime || ''} – ${leave.endTimeLabel || leave.endTime || ''}`.trim();
  }
  return leave.leaveTypeLabel || 'Full day leave';
};

const normalizeCompletedTask = (task) => ({
  ...task,
  status: 'COMPLETED',
});

const CompletedTasksColumn = ({ tasks }) => {
  const completed = (tasks || []).map(normalizeCompletedTask);
  const totalMinutes = completed.reduce((sum, t) => sum + (Number(t.minutes) > 0 ? Number(t.minutes) : 0), 0);

  return (
    <section className="rounded-xl border border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-900/60 overflow-hidden shadow-sm">
      <div className="px-3 py-2.5 border-b bg-blue-100 dark:bg-blue-950/50 text-blue-900 dark:text-blue-100 border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-blue-500" aria-hidden />
            <h4 className="text-[11px] font-bold uppercase tracking-wider truncate">Tasks Completed Today</h4>
          </div>
          <span className="flex-shrink-0 text-[10px] font-bold tabular-nums opacity-90">
            {completed.length}
            {totalMinutes > 0 ? ` · ${totalMinutes}m` : ''}
          </span>
        </div>
      </div>
      <div className="p-2 space-y-1.5">
        {completed.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 italic px-1 py-3 text-center">
            No tasks completed on this day
          </p>
        ) : (
          completed.map((task) => (
            <div
              key={task.id || task.title}
              className="rounded-lg border border-gray-100 dark:border-gray-700/70 bg-gray-50/90 dark:bg-gray-800/70 px-2.5 py-2 border-l-[3px] border-l-emerald-500"
            >
              <div className="flex items-start gap-2">
                <span className={`flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full ${TASK_STATUS_STYLES.COMPLETED}`}>
                  <TaskStatusIcon status="COMPLETED" className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                    {task.title}
                  </p>
                  <div className="flex items-center justify-between gap-1 mt-1">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${TASK_STATUS_STYLES.COMPLETED}`}>
                      {formatTaskStatusLabel('COMPLETED')}
                    </span>
                    {task.minutes != null && task.minutes > 0 && (
                      <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded tabular-nums text-blue-600 bg-blue-500/10">
                        {task.minutes}m
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
};

const ProductivityBadge = ({ percent }) => {
  if (percent == null) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">—</span>;
  }
  return (
    <div className="flex flex-col items-end gap-1 flex-shrink-0">
      <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{percent}%</span>
      <div className="w-14 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div
          className={`h-full rounded-full ${productivityColor(percent)}`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        Productivity
      </span>
    </div>
  );
};

const userInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const ShareUserAvatar = ({ share, sizeClass = 'w-8 h-8', textClass = 'text-xs' }) => {
  const name = share?.ownerUserName || share?.ownerUserId || 'User';
  return (
    <PresignedAvatar
      fileKey={share?.ownerProfilePicture}
      name={name}
      initials={userInitials(name)}
      className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
      fallbackClassName={`${sizeClass} rounded-full flex-shrink-0 bg-emerald-600 flex items-center justify-center text-white ${textClass} font-semibold`}
    />
  );
};

const TeamAttendancePanel = ({ onClose, initialOwnerUserId }) => {
  const currentUser = useSelector((state) => state.auth?.user);
  const isManager = currentUser?.role === 'ADMIN' || currentUser?.role === 'ORG_ADMIN';
  const [sharedUsers, setSharedUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(initialOwnerUserId || '');
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [progress, setProgress] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(true);
  const [decidingLeaveId, setDecidingLeaveId] = useState('');
  const [error, setError] = useState('');
  const [expandedDates, setExpandedDates] = useState({});
  const [dayBriefing, setDayBriefing] = useState(null);
  const [dayCompletedTasks, setDayCompletedTasks] = useState([]);
  const [dayComment, setDayComment] = useState('');
  const [dayCompletedMeta, setDayCompletedMeta] = useState(null);
  const [dayHeaderSubtitle, setDayHeaderSubtitle] = useState('');
  const [dayStats, setDayStats] = useState(null);
  const [detailUserName, setDetailUserName] = useState('');
  const [openingDay, setOpeningDay] = useState(false);
  const [dayTimeline, setDayTimeline] = useState(null);
  const [openingTimeline, setOpeningTimeline] = useState(false);
  const [teamLiveStatus, setTeamLiveStatus] = useState([]);
  const [loadingLiveStatus, setLoadingLiveStatus] = useState(true);
  const [teamRosterExpanded, setTeamRosterExpanded] = useState(true);
  const [breakReason, setBreakReason] = useState('Lunch');
  const [leaveReason, setLeaveReason] = useState('');
  const [acting, setActing] = useState('');
  const [summaryMember, setSummaryMember] = useState(null);
  const userPickerRef = useRef(null);

  const endDate = useMemo(() => formatDateInput(new Date()), []);
  const startDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return formatDateInput(d);
  }, []);

  const selectedLive = useMemo(
    () => teamLiveStatus.find((member) => member.userId === selectedUserId) || null,
    [teamLiveStatus, selectedUserId],
  );

  const selectedShare = useMemo(
    () => sharedUsers.find((u) => u.ownerUserId === selectedUserId) || null,
    [sharedUsers, selectedUserId],
  );

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return sharedUsers;
    return sharedUsers.filter((share) => {
      const name = (share.ownerUserName || '').toLowerCase();
      const id = (share.ownerUserId || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [sharedUsers, userSearch]);

  useEffect(() => {
    if (!userPickerOpen) return undefined;
    const onPointerDown = (event) => {
      if (userPickerRef.current && !userPickerRef.current.contains(event.target)) {
        setUserPickerOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setUserPickerOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [userPickerOpen]);

  const loadPendingApprovals = useCallback(async () => {
    setLoadingApprovals(true);
    try {
      const data = await fetchPendingLeaveApprovals();
      setPendingLeaves(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load leave approvals');
    } finally {
      setLoadingApprovals(false);
    }
  }, []);

  const handleStartSharedBreak = async () => {
    if (!selectedUserId || !breakReason) return;
    setActing('break');
    setError('');
    try {
      await startSharedUserBreak(selectedUserId, breakReason);
      await loadTeamLiveStatus({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to start break');
    } finally {
      setActing('');
    }
  };

  const handleStopSharedBreak = async () => {
    if (!selectedUserId) return;
    setActing('break');
    setError('');
    try {
      await endSharedUserBreak(selectedUserId);
      await loadTeamLiveStatus({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to stop break');
    } finally {
      setActing('');
    }
  };

  const onLeaveNow = selectedLive?.attendanceState === 'ON_LEAVE'
    || selectedLive?.presenceStatus === 'ON_LEAVE';

  const handleRevertLeave = async () => {
    if (!selectedUserId) return;
    setActing('leave');
    setError('');
    try {
      await revertSharedUserLeave(selectedUserId);
      await loadTeamLiveStatus({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to revert leave');
    } finally {
      setActing('');
    }
  };

  const handleMarkOnLeave = async () => {
    if (!selectedUserId) return;
    setActing('leave');
    setError('');
    try {
      await markSharedUserOnLeave({
        userId: selectedUserId,
        leaveType: 'FULL_DAY',
        reason: leaveReason.trim() || 'Marked on leave',
      });
      setLeaveReason('');
      await loadTeamLiveStatus({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to mark on leave');
    } finally {
      setActing('');
    }
  };

  const loadTeamLiveStatus = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingLiveStatus(true);
    try {
      const data = await fetchTeamLiveStatus();
      setTeamLiveStatus(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load team live status', err);
      if (!silent) setTeamLiveStatus([]);
    } finally {
      if (!silent) setLoadingLiveStatus(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingUsers(true);
      setError('');
      try {
        const users = await fetchSharedWithMe();
        if (cancelled) return;
        setSharedUsers(users || []);
        if (users?.length > 0) {
          setSelectedUserId((prev) => {
            if (initialOwnerUserId && users.some((u) => u.ownerUserId === initialOwnerUserId)) {
              return initialOwnerUserId;
            }
            return prev || users[0].ownerUserId;
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load shared users');
        }
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    };
    load();
    loadPendingApprovals();
    loadTeamLiveStatus();
    return () => {
      cancelled = true;
    };
  }, [loadPendingApprovals, loadTeamLiveStatus, initialOwnerUserId]);

  useEffect(() => {
    const applyLivePatch = (detail) => {
      if (!detail?.userId || !detail.patch) return;
      setTeamLiveStatus((prev) => {
        const idx = prev.findIndex((m) => String(m.userId) === String(detail.userId));
        if (idx === -1) return prev;
        const next = [...prev];
        const merged = { ...next[idx], ...detail.patch };
        if (detail.patch.onBreak === false && merged.attendanceState === 'ON_BREAK') {
          merged.attendanceState = detail.patch.attendanceState || 'CHECKED_IN';
        }
        if (detail.patch.onBreak === false) {
          merged.onBreak = false;
        }
        if (detail.patch.presenceStatus === 'ON_LEAVE') {
          merged.attendanceState = 'ON_LEAVE';
        }
        if (detail.patch.presenceLabel) {
          merged.presenceLabel = detail.patch.presenceLabel;
        }
        next[idx] = merged;
        return next;
      });
    };

    return subscribeTeamLiveStatusChanged(applyLivePatch);
  }, []);

  useEffect(() => {
    if (!initialOwnerUserId) return;
    if (sharedUsers.some((u) => u.ownerUserId === initialOwnerUserId)) {
      setSelectedUserId(initialOwnerUserId);
    }
  }, [initialOwnerUserId, sharedUsers]);

  useEffect(() => {
    if (!selectedUserId) {
      setProgress(null);
      return;
    }

    let cancelled = false;
    const loadProgress = async () => {
      setLoadingProgress(true);
      setError('');
      setExpandedDates({});
      try {
        const data = await fetchTeamAttendanceProgress(selectedUserId, startDate, endDate);
        if (!cancelled) setProgress(data);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load attendance progress');
        }
      } finally {
        if (!cancelled) setLoadingProgress(false);
      }
    };
    loadProgress();
    return () => { cancelled = true; };
  }, [selectedUserId, startDate, endDate]);

  const handleLeaveDecision = async (leaveId, approve) => {
    setDecidingLeaveId(leaveId);
    setError('');
    try {
      if (approve) {
        await approveLeave(leaveId);
      } else {
        await rejectLeave(leaveId);
      }
      await loadPendingApprovals();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || `Failed to ${approve ? 'approve' : 'reject'} leave`);
    } finally {
      setDecidingLeaveId('');
    }
  };

  const toggleDay = (date, event) => {
    event?.stopPropagation();
    setExpandedDates((prev) => ({ ...prev, [date]: !prev[date] }));
  };

  const mapSummaryTasks = (tasks = []) => tasks.map((task) => ({
    id: task.id,
    title: task.title || 'Task',
    status: task.status || 'COMPLETED',
    minutes: task.minutes ?? task.estimateTimeInMinutes ?? null,
  }));

  const mergeLiveTasksIntoBriefing = (briefing, day) => {
    if (day.status !== 'CHECKED_IN') {
      return briefing;
    }
    const activeById = new Map((day.activeTasks || []).map((task) => [task.id, task]));
    const mergedTasks = (briefing.tasks || []).map((item) => {
      const live = activeById.get(item.id);
      if (!live) return item;
      return {
        ...item,
        title: live.title || item.title,
        subtitle: formatTaskStatusLabel(live.status),
        minutes: live.minutes ?? item.minutes,
      };
    });
    const existingIds = new Set(mergedTasks.map((task) => task.id));
    const extraActive = (day.activeTasks || [])
      .filter((task) => !existingIds.has(task.id))
      .map((task) => ({
        id: task.id,
        title: task.title || 'Task',
        subtitle: formatTaskStatusLabel(task.status),
        minutes: task.minutes ?? null,
      }));
    return {
      ...briefing,
      dayLabel: briefing.dayLabel || day.dayLabel,
      dateLabel: briefing.dateLabel || day.dateLabel,
      date: briefing.date || day.date,
      dayType: briefing.dayType || day.dayType,
      tasks: [...mergedTasks, ...extraActive],
    };
  };

  const closeDayPopup = () => {
    setDayBriefing(null);
    setDayCompletedTasks([]);
    setDayComment('');
    setDayCompletedMeta(null);
    setDayHeaderSubtitle('');
    setDayStats(null);
  };

  const buildDayStats = (day, summary) => ({
    logoutTime: summary?.logoutTime || day.checkOutTime || null,
    totalTimeSpent: summary?.totalTimeSpent || day.totalTimeSpent || '0m',
    totalBreakTime: summary?.totalBreakTime
      || day.totalBreakTime
      || (day.totalBreakMinutes ? `${day.totalBreakMinutes}m` : '0m'),
    checkInTime: summary?.checkInTime || day.checkInTime || '—',
    productivityPercent: summary?.productivityPercent ?? day.productivityPercent,
  });

  const openDayTimeline = async (day, e) => {
    e?.stopPropagation?.();
    const ownerId = progress?.userId || selectedUserId;
    if (!ownerId || !day?.date) return;
    setOpeningTimeline(true);
    setError('');
    try {
      const data = await fetchDayActivityTimeline(day.date, ownerId);
      setDayTimeline(data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open day timeline');
    } finally {
      setOpeningTimeline(false);
    }
  };

  const openDayPopup = async (day) => {
    const userName = progress?.userName
      || sharedUsers.find((u) => u.ownerUserId === selectedUserId)?.ownerUserName
      || 'User';
    setDetailUserName(userName);
    setOpeningDay(true);
    setError('');
    try {
      if (!day.checkInEventId) {
        throw new Error('No check-in record found for this day');
      }

      const briefing = await fetchAttendanceEventBriefing(day.checkInEventId);
      let completedTasks = mapSummaryTasks(day.completedTasks);
      let summary = null;

      if (day.checkOutEventId) {
        summary = await fetchAttendanceEventLogoutSummary(day.checkOutEventId);
        if (completedTasks.length === 0) {
          completedTasks = mapSummaryTasks(summary.completedTasks);
        }
      }

      setDayBriefing(mergeLiveTasksIntoBriefing(briefing, day));
      setDayCompletedTasks(completedTasks);
      setDayComment(summary?.comment || '');
      setDayCompletedMeta(summary ? {
        completedRemindersCount: summary.completedRemindersCount ?? 0,
        totalCompletedReminderMinutes: summary.totalCompletedReminderMinutes ?? 0,
      } : null);
      setDayStats(buildDayStats(day, summary));
      setDayHeaderSubtitle(
        day.status === 'LOGGED_OUT' ? 'Attendance record' : "Today's work in progress",
      );
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open day details');
    } finally {
      setOpeningDay(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center space-x-2 min-w-0">
          <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Team Attendance</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              Approvals, productivity & completed tasks
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
          title="Close panel"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-3 space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <section className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/25 overflow-hidden">
          <button
            type="button"
            onClick={() => setTeamRosterExpanded((open) => !open)}
            className="w-full px-3 py-2.5 flex items-center justify-between gap-2 text-left hover:bg-emerald-100/50 dark:hover:bg-emerald-900/30 transition-colors"
            aria-expanded={teamRosterExpanded}
          >
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                Team live status
              </p>
              <p className="text-xs text-emerald-900/80 dark:text-emerald-100/80 mt-0.5 truncate">
                {loadingLiveStatus
                  ? 'Loading…'
                  : `${teamLiveStatus.length} people · check-in & presence`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!loadingLiveStatus && teamLiveStatus.length > 0 && (
                <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-semibold text-emerald-800 dark:text-emerald-200">
                  <span className="tabular-nums">
                    {teamLiveStatus.filter((m) => m.attendanceState === 'CHECKED_IN' || m.attendanceState === 'ON_BREAK').length}
                    {' in'}
                  </span>
                  <span className="text-emerald-600/50 dark:text-emerald-400/50">·</span>
                  <span className="tabular-nums">
                    {teamLiveStatus.filter((m) => m.attendanceState === 'NOT_CHECKED_IN' || m.attendanceState === 'LOGGED_OUT').length}
                    {' out'}
                  </span>
                </span>
              )}
              <svg
                className={`w-4 h-4 text-emerald-700 dark:text-emerald-300 transition-transform ${teamRosterExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {teamRosterExpanded && (
            <div className="px-2.5 pb-2.5 space-y-1.5 border-t border-emerald-200/80 dark:border-emerald-800/80 pt-2">
              {loadingLiveStatus ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 py-3 text-center">Loading team status…</p>
              ) : teamLiveStatus.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 py-3 text-center">
                  No one in your reporting line yet. Assign a supervisor on their profile.
                </p>
              ) : (
                teamLiveStatus.map((member) => {
                  const state = member.attendanceState || 'NOT_CHECKED_IN';
                  const selected = member.userId === selectedUserId;
                  return (
                    <div
                      key={member.userId}
                      className={`flex items-stretch gap-1 rounded-lg border ${
                        selected
                          ? 'border-emerald-400 dark:border-emerald-600 bg-white dark:bg-gray-900 shadow-sm'
                          : 'border-emerald-100 dark:border-emerald-900/50 bg-white/80 dark:bg-gray-900/50'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedUserId(member.userId)}
                        className="min-w-0 flex-1 flex items-center gap-2.5 px-2.5 py-2 text-left hover:bg-white dark:hover:bg-gray-900 rounded-l-lg"
                      >
                      <div className="relative flex-shrink-0">
                        <PresignedAvatar
                          fileKey={member.profilePicture}
                          name={member.userName || 'User'}
                          initials={userInitials(member.userName)}
                          className="w-9 h-9 rounded-full object-cover"
                          fallbackClassName="w-9 h-9 rounded-full flex items-center justify-center bg-emerald-600 text-white text-xs font-semibold"
                        />
                        <span
                          className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-gray-900 ${presenceDotClass(member)}`}
                          title={member.presenceLabel || member.presenceStatus}
                          aria-hidden
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {member.userName || 'User'}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {member.presenceLabel || member.presenceStatus || 'Offline'}
                          {member.checkInTime ? ` · in ${member.checkInTime}` : ''}
                        </p>
                      </div>
                      <span
                        className={`flex-shrink-0 inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                          ATTENDANCE_STATE_STYLES[state] || ATTENDANCE_STATE_STYLES.NOT_CHECKED_IN
                        }`}
                      >
                        {ATTENDANCE_STATE_LABELS[state] || 'Not checked in'}
                      </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSummaryMember(member)}
                        className="flex-shrink-0 px-2 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 rounded-r-lg"
                      >
                        Summary
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/70 dark:bg-violet-950/30 overflow-hidden">
          <div className="px-3 py-2.5 border-b border-violet-200 dark:border-violet-800 flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
                Leave approvals
              </p>
              <p className="text-xs text-violet-900/80 dark:text-violet-100/80 mt-0.5">
                From people who shared attendance & status with you
              </p>
            </div>
            {!loadingApprovals && pendingLeaves.length > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-600 text-white tabular-nums">
                {pendingLeaves.length}
              </span>
            )}
          </div>
          <div className="p-2.5 space-y-2">
            {loadingApprovals ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 py-2 text-center">Loading approvals…</p>
            ) : pendingLeaves.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 py-2 text-center">
                No pending leave requests
              </p>
            ) : (
              pendingLeaves.map((leave) => (
                <div
                  key={leave.id}
                  className="rounded-lg border border-violet-200/80 dark:border-violet-800/80 bg-white dark:bg-gray-900/70 px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {leave.requesterUserName || 'Teammate'}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                      {leaveSummaryLine(leave)}
                    </p>
                    {leave.reason && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {leave.reason}
                      </p>
                    )}
                  </div>
                  <div className="mt-2.5 flex items-center gap-2">
                    <button
                      type="button"
                      disabled={Boolean(decidingLeaveId)}
                      onClick={() => handleLeaveDecision(leave.id, true)}
                      className="flex-1 text-xs font-bold px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {decidingLeaveId === leave.id ? 'Saving…' : 'Approve'}
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(decidingLeaveId)}
                      onClick={() => handleLeaveDecision(leave.id, false)}
                      className="flex-1 text-xs font-bold px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 disabled:opacity-60"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {loadingUsers ? (
          <div className="flex items-center justify-center py-12 text-sm text-gray-500 dark:text-gray-400">
            Loading shared users…
          </div>
        ) : sharedUsers.length === 0 ? (
          <div className="text-center py-10 px-4">
            <p className="text-sm font-medium text-gray-900 dark:text-white">No shared attendance yet</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              People who report to you show here automatically. Extra shares can still be granted from Share Attendance.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                Select user
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0" ref={userPickerRef}>
                  <button
                    type="button"
                    onClick={() => setUserPickerOpen((open) => !open)}
                    aria-haspopup="listbox"
                    aria-expanded={userPickerOpen}
                    className="w-full flex items-center gap-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-2 hover:border-emerald-400 dark:hover:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-colors"
                  >
                    {selectedShare ? (
                      <ShareUserAvatar share={selectedShare} />
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                    )}
                    <span className="flex-1 min-w-0 text-left font-semibold text-gray-900 dark:text-white truncate">
                      {selectedShare?.ownerUserName || selectedShare?.ownerUserId || 'Choose a user'}
                    </span>
                    <svg
                      className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${userPickerOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {userPickerOpen && (
                    <div className="absolute z-30 mt-1.5 w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 shadow-lg overflow-hidden">
                      <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                        <div className="relative">
                          <svg
                            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
                          </svg>
                          <input
                            type="search"
                            autoFocus
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            placeholder="Search user…"
                            className="w-full text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg pl-8 pr-2.5 py-1.5 outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                            aria-label="Search users in dropdown"
                          />
                        </div>
                      </div>
                      <ul role="listbox" className="max-h-56 overflow-y-auto py-1">
                        {filteredUsers.length === 0 ? (
                          <li className="px-3 py-3 text-xs text-center text-gray-500 dark:text-gray-400">
                            No users match
                          </li>
                        ) : (
                          filteredUsers.map((share) => {
                            const isSelected = share.ownerUserId === selectedUserId;
                            const name = share.ownerUserName || share.ownerUserId;
                            return (
                              <li key={share.id || share.ownerUserId} role="option" aria-selected={isSelected}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedUserId(share.ownerUserId);
                                    setUserPickerOpen(false);
                                    setUserSearch('');
                                  }}
                                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 text-left transition-colors ${
                                    isSelected
                                      ? 'bg-emerald-50 dark:bg-emerald-950/40'
                                      : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                                  }`}
                                >
                                  <ShareUserAvatar share={share} />
                                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
                                    {name}
                                  </span>
                                  {isSelected && (
                                    <svg className="w-4 h-4 flex-shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </button>
                              </li>
                            );
                          })
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  disabled={!selectedUserId}
                  onClick={() => {
                    const params = new URLSearchParams({
                      userId: selectedUserId,
                      userName: selectedShare?.ownerUserName || progress?.userName || '',
                      startDate,
                      endDate,
                    });
                    window.open(
                      `/attendance/analytics?${params.toString()}`,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }}
                  className="flex-shrink-0 p-2 rounded-lg border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-50 transition-colors"
                  title="Open analytics table & charts"
                  aria-label="Open analytics table and charts"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </button>
              </div>
            </div>

            {selectedUserId && (
              <div className="rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50/70 dark:bg-sky-950/30 px-3 py-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-sky-700 dark:text-sky-300">
                    Attendance actions
                  </p>
                  <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                    ATTENDANCE_STATE_STYLES[selectedLive?.attendanceState] || ATTENDANCE_STATE_STYLES.NOT_CHECKED_IN
                  }`}>
                    {ATTENDANCE_STATE_LABELS[selectedLive?.attendanceState] || 'Unknown'}
                  </span>
                </div>
                {selectedLive?.checkedIn && !selectedLive?.loggedOut && (
                  <div className="flex flex-wrap items-center gap-2">
                    {selectedLive.onBreak ? (
                      <button
                        type="button"
                        disabled={acting === 'break'}
                        onClick={handleStopSharedBreak}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
                      >
                        {acting === 'break' ? 'Stopping…' : 'Stop break'}
                      </button>
                    ) : (
                      <>
                        <select
                          value={breakReason}
                          onChange={(e) => setBreakReason(e.target.value)}
                          className="text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5"
                          aria-label="Break reason"
                        >
                          {SHORT_BREAK_REASONS.filter((reason) => reason !== 'Add your reason').map((reason) => (
                            <option key={reason} value={reason}>{reason}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={acting === 'break'}
                          onClick={handleStartSharedBreak}
                          className="text-xs font-bold px-3 py-1.5 rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60"
                        >
                          {acting === 'break' ? 'Starting…' : 'Start break'}
                        </button>
                      </>
                    )}
                  </div>
                )}
                {isManager && onLeaveNow && (
                  <button
                    type="button"
                    disabled={acting === 'leave'}
                    onClick={handleRevertLeave}
                    className="text-xs font-bold px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/40 disabled:opacity-60"
                  >
                    {acting === 'leave' ? 'Reverting…' : 'Revert leave'}
                  </button>
                )}
                {isManager && !onLeaveNow && (
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={leaveReason}
                      onChange={(e) => setLeaveReason(e.target.value)}
                      placeholder="Leave reason"
                      className="flex-1 min-w-[8rem] text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5"
                    />
                    <button
                      type="button"
                      disabled={acting === 'leave'}
                      onClick={handleMarkOnLeave}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60"
                    >
                      {acting === 'leave' ? 'Saving…' : 'Mark on leave'}
                    </button>
                  </div>
                )}
                {!selectedLive?.checkedIn && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Break can be started after this user checks in.
                  </p>
                )}
              </div>
            )}

            {loadingProgress ? (
              <div className="flex items-center justify-center py-12 text-sm text-gray-500 dark:text-gray-400">
                Loading progress…
              </div>
            ) : progress?.days?.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                No attendance entries in the last 30 days.
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {progress?.userName} · chevron expands tasks · tap row for briefing · timeline icon for day line
                </p>
                {progress?.days?.map((day) => {
                  const isExpanded = Boolean(expandedDates[day.date]);
                  const completedTasks = day.completedTasks || [];

                  return (
                    <div
                      key={day.date}
                      className={`rounded-xl border overflow-hidden transition-colors ${
                        isExpanded
                          ? 'border-emerald-300 dark:border-emerald-700 bg-gradient-to-b from-emerald-50/80 to-white dark:from-emerald-950/30 dark:to-gray-900'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40'
                      }`}
                    >
                      <div className="flex items-stretch">
                        <button
                          type="button"
                          onClick={(e) => toggleDay(day.date, e)}
                          className="flex-shrink-0 px-3 py-3 text-gray-500 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-gray-900/60 transition-colors"
                          aria-label={isExpanded ? 'Collapse tasks' : 'Expand tasks'}
                          aria-expanded={isExpanded}
                        >
                          <svg
                            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => openDayPopup(day)}
                          disabled={openingDay}
                          className="flex-1 text-left px-1 py-3 pr-2 flex items-center justify-between gap-3 hover:bg-white/60 dark:hover:bg-gray-900/60 transition-colors disabled:opacity-60"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              {day.dateLabel}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {day.dayLabel}
                              {day.status === 'CHECKED_IN' && (
                                <span className={`ml-2 inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_STYLES.CHECKED_IN}`}>
                                  {STATUS_LABELS.CHECKED_IN}
                                </span>
                              )}
                            </p>
                          </div>
                          <ProductivityBadge percent={day.productivityPercent} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => openDayTimeline(day, e)}
                          disabled={openingTimeline}
                          className="flex-shrink-0 px-3 py-3 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors disabled:opacity-50"
                          title="Day timeline — login, breaks, logout"
                          aria-label={`Open day timeline for ${day.dateLabel || day.date}`}
                        >
                          <svg className="w-4.5 h-4.5 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h7" />
                          </svg>
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="px-3 pb-3 pt-1 border-t border-emerald-200/60 dark:border-emerald-800/60">
                          <CompletedTasksColumn tasks={completedTasks} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {dayBriefing && (
        <LogToWorkModal
          briefing={dayBriefing}
          userName={detailUserName}
          readOnly
          completedTasks={dayCompletedTasks}
          comment={dayComment}
          completedMeta={dayCompletedMeta}
          headerSubtitle={dayHeaderSubtitle}
          dayStats={dayStats}
          onClose={closeDayPopup}
          onOpenTimeline={() => {
            const day = {
              date: dayBriefing?.date,
              dateLabel: dayBriefing?.dateLabel,
            };
            if (!day.date) return;
            closeDayPopup();
            openDayTimeline(day);
          }}
        />
      )}

      {dayTimeline && (
        <DayActivityTimelineModal
          timeline={dayTimeline}
          onClose={() => setDayTimeline(null)}
          onChangeDate={async (nextDate) => {
            if (!selectedUserId) return;
            const data = await fetchDayActivityTimeline(nextDate, progress?.userId || selectedUserId);
            setDayTimeline(data);
          }}
        />
      )}

      {summaryMember ? (
        <MemberSummaryModal
          member={summaryMember}
          onClose={() => setSummaryMember(null)}
        />
      ) : null}
    </div>
  );
};

export default TeamAttendancePanel;
